import React from 'react';
import { ProjectInfrastructureOwnerEnum } from '@lib/api-interface';
import { DataTable, HStack, Panel, ToggleButtons } from '@lib/ui-components';

import { useProjectBranchContext, useProjectBranchSettingsContext } from '@context';
import { useTranslation } from 'react-i18next';
import { InfrastructureCategorySelectOptionsFactory } from '../../../../../factories/infrastructure-category-select-options.factory';
import { useUserState } from '../../../../../state/UserState';
import { RolesEnum } from '../../../../../domain/enums/roles.enum';
import { AuthoritiesEnum } from '../../../../../domain/enums/authorities.enum';

interface InfrastructureServicesProps {
  withMainTableTitle?: boolean;
}

export const InfrastructureServices = (props: InfrastructureServicesProps) => {
  const [tableReloadKey] = React.useState(1);
  const [activePage, setActivePage] = React.useState(0);
  const userState = useUserState();
  const clientUser = userState.roles.includes(RolesEnum.CLIENT_USER);
  const regionalAdmin = userState.roles.includes(RolesEnum.REGIONAL_ADMIN);
  const projectSettings = useProjectBranchSettingsContext();
  const infrastructurePriceHidden =
    (clientUser && !userState.authorities.includes(AuthoritiesEnum.SEES_PRICES)) || (regionalAdmin && !projectSettings?.regionalAdminSeePricesAndCanGenerateReports);

  const projectBranchViewModel = useProjectBranchContext();
  const { t } = useTranslation();

  return (
    <Panel header={props.withMainTableTitle ? t('projectInfrastructure') : undefined} borderType='raised'>
      <HStack align='center'>
        <ToggleButtons labels={[t('active'), t('inactive')]} activeIndex={activePage} onChange={(idx) => setActivePage(idx)} />
      </HStack>
      <DataTable
        excel={true}
        refreshKey={tableReloadKey}
        fetchPostUrl={`/api/client-infrastructure-services/by-project-branch/${projectBranchViewModel.projectBranchId}`}
        fetchFilters={{ active: activePage === 0 }}
        noFilters={true}
        columns={[
          {
            key: 'category',
            label: t('category'),
            type: 'enum',
            enumValues: InfrastructureCategorySelectOptionsFactory.getSelectOptionsWithAll(t),
          },
          {
            key: 'name',
            label: t('resourceName'),
            type: 'text',
          },
          {
            key: 'projectInfrastructureDescription',
            label: t('projectInfrastructureDescription'),
            type: 'text',
          },
          {
            key: 'estimatedMass',
            label: t('estimatedMass'),
            type: 'text',
          },
          {
            key: 'owner',
            label: t('owner'),
            type: 'enum',
            enumValues: [
              { value: ProjectInfrastructureOwnerEnum.CLIENT, label: t('client') },
              { value: ProjectInfrastructureOwnerEnum.SUBCONTRACTOR, label: t('subcontractor') },
              { value: ProjectInfrastructureOwnerEnum.ORGANIZATION, label: t('is') },
              { value: ProjectInfrastructureOwnerEnum.COMMUNE, label: t('commune') },
            ],
          },
          {
            key: 'currentSettings.infrastructureCount',
            label: t('number'),
            type: 'numeric',
          },
          {
            key: 'currentSettings.clientLease',
            label: t('leasePriceClient'),
            type: 'text',
            hidden: infrastructurePriceHidden,
          },
          {
            key: 'commune',
            label: t('commune'),
            type: 'boolean',
          },
          {
            key: 'activationDate',
            label: t('activationDate'),
            type: 'date',
          },
          {
            key: 'deactivationDate',
            label: t('deactivationDate'),
            type: 'date',
          },
        ]}
      />
    </Panel>
  );
};
